import { isDashboardSecretName } from '@developerz/domain';
import { IconPlus, IconTrash } from '@developerz/ui';
import { createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  deleteConnectionToken,
  type SecretRow,
  saveConnectionToken,
  useSecrets,
} from '../lib/connections';
import { createMutation } from '../lib/mutation';
import { createToast } from '../lib/toast';
import { AddConnectionModal } from './AddConnectionModal';
import { ConfirmDialog } from './ConfirmDialog';
import { ConnectionTokenRow } from './ConnectionTokenRow';
import { LoadMore } from './LoadMore';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Toast } from './Toast';
import { Button } from './ui/Button';

// Connections manager (#312), mock Panel "Connections": the account's
// `dashboard:<handle>` vault credentials (`mcp_servers[].token_ref`). Rows show
// refs + dates only (values are sealed and never come back). A failed revoke
// surfaces as an error toast while the confirm stays open. Mounted ONLY behind
// `<ConnectionTokens/>`'s owner gate — every `/v1/secrets` route is owner-only,
// so mounting this at all is already the "may they?" answer (#1418).

export function ConnectionTokensPanel(props: { readonly token: string | null }): JSX.Element {
  const secrets = useSecrets(props.token);
  const [adding, setAdding] = createSignal(false);

  const [revoking, setRevoking] = createSignal<SecretRow | null>(null);
  const toast = createToast();

  // Client-side split, honest ONLY because `useSecrets` exhausts the pages first:
  // `integration:*` rows belong to the catalog panel above, and a `dashboard:*`
  // row sorted onto page two was invisible here — unlistable AND unrevokable.
  const managed = (rows: readonly SecretRow[]): readonly SecretRow[] =>
    rows.filter((s) => isDashboardSecretName(s.name));

  // `reload()`, not `reset()`: page one sits inside the 30s `staleTime`, so
  // re-keying to it would answer a just-written list from a stale cache. It runs
  // on BOTH outcomes here — a rejected add may still have landed a row, and the
  // server's list is the only thing that knows.
  const add = createMutation({
    call: (input: { name: string; value: string }) => saveConnectionToken(input, props.token),
    fallback: t('connections.add_error'),
    onSuccess: () => {
      setAdding(false);
      secrets.reload();
    },
    onError: () => secrets.reload(),
  });

  const revoke = createMutation({
    call: (name: string) => deleteConnectionToken(name, props.token),
    fallback: t('connections.revoke_error'),
    onSuccess: () => {
      setRevoking(null);
      secrets.reload();
    },
    onError: (message) => toast.notify(message, 'error'),
  });

  // One busy state and one add-failure line for both writes, as when they shared
  // a signal — each mutation owns its own, the panel folds them for the markup.
  const busy = (): boolean => add.pending() || revoke.pending();
  const addError = add.error;

  const submitAdd = (input: { name: string; value: string }): void => void add.run(input);

  const confirmRevoke = (): void => {
    const row = revoking();
    if (!row) return;
    void revoke.run(row.name);
  };

  return (
    <>
      <Panel
        title={t('connections.title')}
        class="connections"
        action={
          <Button size="sm" onClick={() => setAdding(true)}>
            <IconPlus />
            {t('connections.add')}
          </Button>
        }
      >
        <p class="connections__copy">
          {t('connections.ref_before')} <code>{t('connections.ref_code')}</code>{' '}
          {t('connections.ref_after')}
        </p>
        <QueryBoundary
          query={secrets.view}
          isEmpty={() => false}
          loading={<p class="list-empty">{t('connections.loading')}</p>}
        >
          {(rows) => (
            <Show
              when={managed(rows).length > 0}
              fallback={<p class="list-empty">{t('connections.empty')}</p>}
            >
              <ul>
                <For each={managed(rows)}>
                  {(row) => <ConnectionTokenRow row={row} onRevoke={setRevoking} />}
                </For>
              </ul>
            </Show>
          )}
        </QueryBoundary>
        {/* Outside the boundary: if `exhaust` hit MAX_AUTO_PAGES the unfetched
            rows are exactly the ones a filtered-empty list is missing, so the
            manual button has to survive the empty state. */}
        <LoadMore list={secrets} />
      </Panel>
      <AddConnectionModal
        open={adding()}
        busy={busy()}
        error={addError()}
        onSubmit={submitAdd}
        onClose={() => {
          setAdding(false);
          add.clearError(); // stale failure must not greet the next open
        }}
      />
      <ConfirmDialog
        open={revoking() !== null}
        title={t('connections.revoke_title')}
        desc={t('connections.revoke_body', { name: revoking()?.name ?? '' })}
        media={<IconTrash />}
        cancelLabel={t('connections.cancel')}
        actionLabel={t('connections.revoke')}
        destructive
        onCancel={() => setRevoking(null)}
        onConfirm={confirmRevoke}
      />
      <Toast message={toast.toast()?.message ?? null} tone="error" onDismiss={toast.dismiss} />
    </>
  );
}
